export const ORDER_FORM_FIELDS = ['name', 'phone', 'email', 'address', 'comment'] as const
export type OrderFormFieldKey = typeof ORDER_FORM_FIELDS[number]
export type OrderFormField = {enabled: boolean; required: boolean}
export const ORDER_FORM_TEXT_LIMITS = {title: 100, subtitle: 250} as const
export type OrderForm = {
  title: string
  subtitle: string
  submitText: string
  successTitle: string
  successMessage: string
  fields: Record<OrderFormFieldKey, OrderFormField>
  order: OrderFormFieldKey[]
}

// Consent is always displayed and required; it cannot be disabled or reordered.
export const ORDER_FORM_LABELS: Record<OrderFormFieldKey, string> = {
  name: 'Имя', phone: 'Телефон', email: 'Почта', address: 'Адрес', comment: 'Комментарий'
}

const textOrDefault = (value: unknown, fallback: string) => typeof value === 'string' ? value : fallback
export const createOrderForm = (source?: Partial<OrderForm>): OrderForm => ({
  title: textOrDefault(source?.title, 'Заявка'),
  subtitle: textOrDefault(source?.subtitle, 'Оставьте контактные данные — мы свяжемся с вами'),
  submitText: textOrDefault(source?.submitText, 'Отправить заявку'),
  successTitle: textOrDefault(source?.successTitle, 'Заявка отправлена'),
  successMessage: textOrDefault(source?.successMessage, 'Спасибо! Мы получили вашу заявку. Мы свяжемся с вами по указанным контактным данным'),
  fields: Object.fromEntries(ORDER_FORM_FIELDS.map(key => [key, {
    enabled: source?.fields?.[key]?.enabled === true,
    required: source?.fields?.[key]?.required === true
  }])) as OrderForm['fields'],
  order: [...new Set([...(Array.isArray(source?.order) ? source.order : []), ...ORDER_FORM_FIELDS])]
    .filter(key => ORDER_FORM_FIELDS.includes(key))
})

export type TariffOrderForm = {id: number; orderForm: OrderForm}
export const orderFormsDiffer = (tariffs: TariffOrderForm[]) => {
  const first = JSON.stringify(createOrderForm(tariffs[0]?.orderForm))
  return tariffs.some(tariff => JSON.stringify(createOrderForm(tariff.orderForm)) !== first)
}
export const copyOrderFormToAll = (tariffs: TariffOrderForm[], source: OrderForm) => {
  for (const tariff of tariffs) tariff.orderForm = createOrderForm(source)
}

export type OrderFormErrors = Partial<Record<'title' | 'subtitle' | 'submitText' | 'contacts' | 'successTitle' | 'successMessage', string>>
export const validateOrderForm = (form: OrderForm): OrderFormErrors => {
  const errors: OrderFormErrors = {}
  if (!form.title.trim()) errors.title = 'Введите заголовок'
  else if (Array.from(form.title).length > ORDER_FORM_TEXT_LIMITS.title) {
    errors.title = `Сократите текст до ${ORDER_FORM_TEXT_LIMITS.title} символов`
  }
  if (!form.subtitle.trim()) errors.subtitle = 'Введите подзаголовок'
  else if (Array.from(form.subtitle).length > ORDER_FORM_TEXT_LIMITS.subtitle) {
    errors.subtitle = `Сократите текст до ${ORDER_FORM_TEXT_LIMITS.subtitle} символов`
  }
  if (!form.submitText.trim()) errors.submitText = 'Введите текст кнопки'
  // TODO: уточнить у аналитика обязательность заголовка и сообщения после отправки.
  if (!form.successTitle.trim()) errors.successTitle = 'Введите заголовок'
  if (!form.successMessage.trim()) errors.successMessage = 'Введите сообщение'
  const contacts = [form.fields.phone, form.fields.email]
  if (!contacts.some(field => field.enabled)) errors.contacts = 'Включите телефон или почту'
  else if (!contacts.some(field => field.enabled && field.required)) {
    errors.contacts = 'Сделайте телефон или почту обязательным полем'
  }
  return errors
}
